/* ============================================================================
 * useOperationQueue.ts - Sequential operation processing queue
 * ============================================================================
 *
 * CRITICAL FIX: Prevents race conditions when MCP creates multiple items rapidly
 *
 * Problem: When operations arrive rapidly (e.g., MCP creates group then tasks),
 * React batches state updates causing all callbacks to receive the same stale
 * currentData. Tasks can't find their parent groups because the group create
 * hasn't been applied yet.
 *
 * Solution: Queue operations and process them sequentially, ensuring each
 * operation sees the result of the previous one.
 */

import { useRef, useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import { flushSync } from 'react-dom';
import type { ProjectData } from '@/shared/types/projectData';
import { isNodeOperation } from '@/shared/api/utils/queueManager';
import { applyOperation, sanitizeProjectData } from '@/shared/utils/projectDataUtils';
import { transformRealtimeOperationFromBackend } from '@/shared/utils/fieldNameTransform';
import { log } from '@/shared/utils/logger';
import { normalizeOperationIds } from './handlers/nodeNormalization';
import { syncProjectCacheFromUpdatedData } from './utils/syncProjectCacheFromUpdatedData';
import { filterRealtimeProjectDataForUser } from '@/features/projects/helpers/realtimeVisibilityProjection';
import type { VisibilityTier } from '@/shared/types/projectData';

interface QueuedOperation {
  operation: Record<string, unknown>;
  messageType: string;
  message: Record<string, unknown>;
}

interface UseOperationQueueParams {
  setProjectData: Dispatch<SetStateAction<ProjectData | null>>;
  projectData: ProjectData | null;
  user?: {
    id?: string;
    username?: string;
    role?: string | null;
  } | null;
  updateProjectInCache?: (projectId: string, updatedData: Partial<ProjectData>) => void;
  refreshProject?: (projectId: string) => Promise<void>;
  projectVisibilityDefaultsRef?: MutableRefObject<Record<string, VisibilityTier | undefined>>;
  projectVisibilityDefaults?: Record<string, VisibilityTier | undefined>;
  projectOwnerIdsRef?: MutableRefObject<Record<string, string | undefined>>;
  projectOwnerIds?: Record<string, string | undefined>;
}

/**
 * Hook for sequential operation processing to prevent race conditions
 */
export function useOperationQueue({
  setProjectData,
  projectData,
  user,
  updateProjectInCache,
  refreshProject,
  projectVisibilityDefaultsRef,
  projectVisibilityDefaults = {},
  projectOwnerIdsRef,
  projectOwnerIds = {},
}: UseOperationQueueParams) {

  const operationQueue = useRef<QueuedOperation[]>([]);
  const isProcessing = useRef(false);
  const currentDataRef = useRef<ProjectData | null>(projectData);

  // Update ref when projectData changes
  currentDataRef.current = projectData;

  const getRealtimeProjectVisibilityDefaults = useCallback(
    () => projectVisibilityDefaultsRef?.current || projectVisibilityDefaults,
    [projectVisibilityDefaults, projectVisibilityDefaultsRef]
  );
  const getRealtimeProjectOwnerIds = useCallback(
    () => projectOwnerIdsRef?.current || projectOwnerIds,
    [projectOwnerIds, projectOwnerIdsRef]
  );

  /**
   * Process the next operation in the queue
   */
  const processNextOperation = useCallback(() => {
    if (isProcessing.current || operationQueue.current.length === 0) {
      return;
    }

    isProcessing.current = true;
    const queuedOp = operationQueue.current.shift()!;
    const { messageType, message } = queuedOp;
    const transformedOperation: Record<string, unknown> = transformRealtimeOperationFromBackend(queuedOp.operation);
    if (!isNodeOperation(transformedOperation)) {
      log.warn('OperationQueue', 'Ignoring realtime operation with an unsupported type', {
        type: transformedOperation.type,
      });
      isProcessing.current = false;
      if (operationQueue.current.length > 0) {
        setTimeout(() => processNextOperation(), 0);
      }
      return;
    }
    const operation = transformedOperation;

    log.debug('OperationQueue', 'Processing operation from queue:', {
      type: operation.type,
      nodeId: operation.nodeId,
      queueLength: operationQueue.current.length,
      timestamp: new Date().toISOString()
    });

    try {
      let updatedDataForCache: ProjectData | null = null;
      let normalizedOperationForCache: Record<string, unknown> = operation;

      // CRITICAL: Use flushSync to force synchronous state update
      // This ensures the next operation sees this operation's result
      flushSync(() => {
        setProjectData(currentData => {
          if (!currentData) {
            log.warn('OperationQueue', 'No current data to apply operation to');
            return null;
          }

          // Normalize IDs based on current data shape (namespace handling)
          const idsAreNamespaced = currentData.nodes.some(
            n => typeof n.id === 'string' && n.id.includes(':')
          );

          log.debug('OperationQueue', 'Before operation:', {
            type: operation.type,
            nodesCount: currentData.nodes.length,
            targetNodeId: operation.nodeId,
            messageProjectId: message.projectId,
            idsAreNamespaced,
            messageType: messageType
          });

          const effectiveProjectId = [
            message.projectId,
            message.project_id,
            operation.projectId,
            currentData.projectId,
          ].find((value): value is string => typeof value === 'string' && value.length > 0);

          const normalizedOperation = normalizeOperationIds(
            { ...operation },
            idsAreNamespaced,
            effectiveProjectId || undefined
          );
          if (!isNodeOperation(normalizedOperation)) {
            log.warn('OperationQueue', 'Ignoring realtime operation invalidated by ID normalization', {
              type: normalizedOperation.type,
            });
            return currentData;
          }
          const normalizedOp = normalizedOperation;
          normalizedOperationForCache = normalizedOp;

          log.debug('OperationQueue', 'After normalization:', {
            type: operation.type,
            originalNodeId: operation.nodeId,
            normalizedNodeId: normalizedOp.nodeId,
            nodeExists: normalizedOp.nodeId ? currentData.nodes.some(n => n.id === normalizedOp.nodeId) : false
          });

          // Apply the operation
          const updatedData = sanitizeProjectData(applyOperation(currentData, normalizedOp));
          const visibilityProjectedData = filterRealtimeProjectDataForUser(
            updatedData,
            user || null,
            getRealtimeProjectVisibilityDefaults(),
            getRealtimeProjectOwnerIds()
          );

          log.debug('OperationQueue', 'After operation:', {
            type: operation.type,
            nodesCount: visibilityProjectedData.nodes.length,
            success: visibilityProjectedData !== currentData
          });

          // Update the ref for next operation
          currentDataRef.current = visibilityProjectedData;
          updatedDataForCache = visibilityProjectedData;

          return visibilityProjectedData;
        });
      });

      const completedData = updatedDataForCache;
      if (completedData) {
        syncProjectCacheFromUpdatedData({
          updatedData: completedData,
          operation: normalizedOperationForCache,
          message,
          updateProjectInCache,
          refreshProject,
        });

      }

      log.debug('OperationQueue', 'Operation completed and flushed');
    } catch (error) {
      log.error('OperationQueue', 'Error processing operation:', error);
    } finally {
      isProcessing.current = false;

      // Process next operation if queue is not empty
      if (operationQueue.current.length > 0) {
        // Use setTimeout to break the call stack and prevent stack overflow
        setTimeout(() => processNextOperation(), 0);
      }
    }
  }, [getRealtimeProjectOwnerIds, getRealtimeProjectVisibilityDefaults, refreshProject, setProjectData, updateProjectInCache, user]);

  /**
   * Add an operation to the queue
   */
  const enqueueOperation = useCallback((operation: Record<string, unknown>, messageType: string, message: Record<string, unknown>) => {
    log.debug('OperationQueue', 'Enqueueing operation:', {
      type: operation.type,
      nodeId: operation.nodeId,
      currentQueueLength: operationQueue.current.length
    });

    operationQueue.current.push({ operation, messageType, message });

    // Start processing if not already processing
    if (!isProcessing.current) {
      processNextOperation();
    }
  }, [processNextOperation]);

  /**
   * Check if queue is empty
   */
  const isQueueEmpty = useCallback(() => {
    return operationQueue.current.length === 0 && !isProcessing.current;
  }, []);

  /**
   * Get current queue length
   */
  const getQueueLength = useCallback(() => {
    return operationQueue.current.length;
  }, []);

  return {
    enqueueOperation,
    isQueueEmpty,
    getQueueLength
  };
}
